import { useCallback, useEffect } from 'react';

import { ModalState, useModalStore } from '@stores/useModalStore';
import { OverlayState, useOverlayStore } from '@stores/useOverlayStore';
import { shallow } from 'zustand/shallow';

type UseModalOptions = {
  initialOpen?: boolean;
  routeChange?: boolean;
  lock?: boolean;
  onClose?: () => void;
  trigger?: boolean; // useEffect 호출 여부
};

export const useModal = <T>(
  name: string,
  {
    initialOpen = false,
    routeChange = true,
    trigger = true,
    onClose,
  }: UseModalOptions = {}
): [
  boolean,
  {
    open: (modalState?: T) => void;
    close: OverlayState['close'];
  },
  T | undefined
] => {
  const [{ isOpen, meta }, setModal, setCurrent, current] = useModalStore(
    (state) => [
      (state.map.get(name) ?? { isOpen: initialOpen }) as ModalState<T>,
      state.setModal(name),
      state.setCurrent,
      state.current,
    ]
  );

  const [openOverlay, closeOverlay] = useOverlayStore(
    (state) => [state.open, state.close],
    shallow
  );

  useEffect(() => {
    if (!trigger) {
      return;
    }
    if (isOpen) {
      openOverlay({
        name,
        routeChange,
        onClose: () => {
          setModal({ isOpen: false });
          onClose?.();
        },
      });
    } else {
      if (current) {
        setCurrent(undefined);
      }
    }
  }, [isOpen, trigger]);
  const open = useCallback((meta?: T) => {
    setModal({ isOpen: true, meta });
  }, []);

  return [isOpen, { open, close: closeOverlay }, meta];
};
